import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Shield,
  Smartphone,
  Video,
  Gavel,
  Star,
  ArrowLeft,
  CheckCircle,
  Users,
  Headphones,
  ChevronDown,
} from "lucide-react";
import { FaGooglePlay, FaApple } from "react-icons/fa";

import heroImg from "../../assets/auctionPoster.jpeg";
import phoneImg from "../../assets/phone with numbers.png";
import liveImg from "../../assets/liveAuctionImg.png";
import DownloadModal from "../../components/downloadModal/downloadModal";
import Scene3D from "./components/Scene3D";
import TiltCard from "./components/TiltCard";
import AnimatedCounter from "./components/AnimatedCounter";
import "./home.css";

const ANDROID_URL = "https://play.google.com/store/apps/details?id=com.barakah.barakah.barakah&hl=en-US";
const IOS_URL = "https://apps.apple.com/us/app/%D9%85%D8%B2%D8%A7%D8%AF-%D8%A8%D8%B1%D9%83%D8%A9/id6476919358?l=ar";

const FEATURE_ICONS = [
  <Video className="w-8 h-8" />,
  <Gavel className="w-8 h-8" />,
  <Shield className="w-8 h-8" />,
  <Star className="w-8 h-8" />,
];

const STAT_ICONS = [
  <Gavel className="w-6 h-6" />,
  <Users className="w-6 h-6" />,
  <Star className="w-6 h-6" />,
  <Headphones className="w-6 h-6" />,
];

const STAT_VALUES = [
  { to: 500, suffix: "+" },
  { to: 15000, suffix: "+" },
  { to: 98, suffix: "%" },
  { to: 24, suffix: "/7" },
];

const StoreButton = ({ href, Icon, brand, dark, availableOnLabel }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`btn-shine flex items-center justify-center gap-3 rounded-xl py-4 px-6 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
      dark
        ? "bg-white text-black hover:bg-gray-50"
        : "bg-black text-white hover:bg-gray-900"
    }`}
  >
    <Icon size={24} />
    <div className="[text-align:start]">
      <div className="text-xs opacity-60">{availableOnLabel}</div>
      <div className="font-bold">{brand}</div>
    </div>
  </a>
);

const Home = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const heroRef = useRef(null);
  const phoneSectionRef = useRef(null);

  const features = t("home.features", { returnObjects: true }).map((f, i) => ({
    ...f,
    icon: FEATURE_ICONS[i],
  }));
  const stats = t("home.stats", { returnObjects: true }).map((s, i) => ({
    ...s,
    ...STAT_VALUES[i],
    icon: STAT_ICONS[i],
  }));
  const steps = t("home.steps", { returnObjects: true });
  const checklist = t("home.checklist", { returnObjects: true });

  const { scrollYProgress: phoneProgress } = useScroll({
    target: phoneSectionRef,
    offset: ["start end", "end start"],
  });
  const phoneRotate = useTransform(phoneProgress, [0, 1], [-8, 8]);
  const phoneY = useTransform(phoneProgress, [0, 1], [40, -40]);

  const handleHeroMouseMove = (e) => {
    const el = heroRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  return (
    <div className="home-page">
      <DownloadModal open={modalOpen} setOpen={setModalOpen} />

      {/* ── Hero Section ─────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        onMouseMove={handleHeroMouseMove}
        className="relative min-h-screen overflow-hidden flex items-center pt-28 pb-24 px-4 bg-gradient-to-b from-dark-grey via-surface-dark to-primary-container-dark"
      >
        <Scene3D className="absolute inset-0 z-0" />
        <div className="spotlight z-[1]" />

        {/* ambient blobs */}
        <div className="absolute top-1/4 -start-20 w-96 h-96 bg-primary/20 rounded-full blur-3xl blob-float-slow z-0" />
        <div className="absolute bottom-0 -end-10 w-80 h-80 bg-primary/15 rounded-full blur-3xl blob-float z-0" />

        <div className="relative z-10 max-w-6xl mx-auto w-full">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Text Side */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex-1 [text-align:start]"
            >
              <motion.span
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass-card-dark inline-block text-medium-blue font-semibold px-4 py-1.5 rounded-full text-sm mb-6"
              >
                {t("home.badge")}
              </motion.span>
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-black text-white leading-tight mb-6">
                {t("home.heroTitlePrefix")} <span className="gradient-text">{t("home.heroTitleHighlight")}</span>
                <br />
                {t("home.heroTitleSuffix")}
              </h1>
              <p className="text-xl text-white/70 leading-relaxed mb-10 max-w-lg">
                {t("home.heroDesc")}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <StoreButton href={ANDROID_URL} Icon={FaGooglePlay} brand="Google Play" availableOnLabel={t("home.storeAvailableOn")} />
                <StoreButton href={IOS_URL} Icon={FaApple} brand="App Store" availableOnLabel={t("home.storeAvailableOn")} />
              </div>

              <div className="flex flex-wrap items-center gap-6 mt-10 text-sm text-white/60">
                {checklist.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Image Side — 3D tilt glass panel */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex-1 perspective-1000"
            >
              <TiltCard className="rounded-xl" intensity={10}>
                <div className="relative glass-card-dark rounded-xl p-3">
                  <img
                    src={heroImg}
                    alt={t("home.heroImgAlt")}
                    className="rounded-xl w-full object-cover max-h-[460px] shadow-2xl"
                  />
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.9, type: "spring" }}
                    className="relative sm:absolute w-fit mx-auto -mt-8 sm:mt-0 sm:-bottom-6 sm:-start-6 bg-white rounded-xl p-2.5 sm:p-4 shadow-2xl border border-primary/20"
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 shrink-0 bg-primary rounded-full flex items-center justify-center">
                        <Gavel className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <div className="whitespace-nowrap">
                        <div className="font-bold text-dark-grey text-xs sm:text-sm">{t("home.heroFloatingBadgeCount")}</div>
                        <div className="text-[10px] sm:text-xs text-grey">{t("home.heroFloatingBadgeLabel")}</div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </TiltCard>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 text-white/50"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <span className="text-xs">{t("home.scrollHint")}</span>
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </section>

      {/* ── Stats strip — overlaps hero/features boundary ───────────── */}
      <section className="relative px-4 z-20">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto glass-card rounded-xl shadow-2xl -mt-16 relative grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-grey/30 rtl:divide-x-reverse"
        >
          {stats.map((s, i) => (
            <div key={i} className="flex flex-col items-center justify-center gap-2 py-8 px-4 text-center">
              <div
                className={`w-11 h-11 rounded-xl flex items-center justify-center mb-1 ${
                  s.accent === "secondary" ? "bg-secondary/10 text-secondary" : "bg-primary/10 text-primary"
                }`}
              >
                {s.icon}
              </div>
              <div className="text-2xl md:text-3xl font-black text-dark-grey">
                <AnimatedCounter to={s.to} suffix={s.suffix} />
              </div>
              <div className="text-xs md:text-sm text-grey">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ── Features Section ─────────────────────────────────────────── */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <span className="text-primary font-bold text-sm tracking-wide">{t("home.featuresEyebrow")}</span>
            <h2 className="text-3xl md:text-4xl font-black text-dark-grey mt-2 mb-4">
              {t("home.featuresTitlePrefix")} <span className="gradient-text">{t("home.featuresTitleHighlight")}</span>{t("home.featuresTitleSuffix")}
            </h2>
            <p className="text-grey text-lg max-w-xl mx-auto">
              {t("home.featuresDesc")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 perspective-1000">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <TiltCard
                  className="rounded-xl h-full bg-light-grey border border-transparent hover:border-primary/20 transition-colors duration-300"
                  intensity={12}
                >
                  <div className="p-6 text-center rounded-xl h-full hover:shadow-xl transition-shadow duration-300">
                    <div
                      className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 text-white shadow-lg ${
                        feature.accent === "secondary"
                          ? "bg-gradient-to-br from-secondary to-secondary/70 shadow-secondary/30"
                          : "bg-gradient-to-br from-primary to-medium-blue shadow-primary/30"
                      }`}
                    >
                      {feature.icon}
                    </div>
                    <h3 className="font-bold text-dark-grey text-lg mb-2">{feature.title}</h3>
                    <p className="text-grey text-sm leading-relaxed">{feature.desc}</p>
                  </div>
                </TiltCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Live Auctions Preview CTA ────────────────────────────────── */}
      <section className="py-24 px-4 bg-gradient-to-r from-primary/10 to-primary/5 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex-1 perspective-1000"
            >
              <TiltCard className="rounded-xl" intensity={10}>
                <img
                  src={liveImg}
                  alt={t("home.liveImgAlt")}
                  className="rounded-xl shadow-2xl w-full object-cover"
                />
              </TiltCard>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex-1 [text-align:start]"
            >
              <span className="relative inline-flex items-center gap-2 bg-error/10 text-error font-semibold px-4 py-1.5 rounded-full text-sm mb-4">
                <span className="pulse-dot relative w-2 h-2 rounded-full bg-error" />
                {t("home.liveBadge")}
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-dark-grey mb-4">
                {t("home.liveTitle")}
              </h2>
              <p className="text-grey leading-relaxed mb-8">
                {t("home.liveDesc")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate("/auctions")}
                  className="btn-shine flex items-center justify-center gap-2 bg-primary text-white rounded-xl py-3 px-6 font-bold hover:bg-primary-dark transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-primary/30"
                >
                  <span>{t("home.browseAuctions")}</span>
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setModalOpen(true)}
                  className="flex items-center justify-center gap-2 border-2 border-primary text-primary rounded-xl py-3 px-6 font-bold hover:bg-primary hover:text-white transition-all duration-200"
                >
                  <Smartphone className="w-5 h-5" />
                  <span>{t("home.downloadAppShort")}</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────── */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-primary font-bold text-sm tracking-wide">{t("home.stepsEyebrow")}</span>
            <h2 className="text-3xl md:text-4xl font-black text-dark-grey mt-2 mb-4">
              {t("home.stepsTitle")}
            </h2>
            <p className="text-grey text-lg">{t("home.stepsDesc")}</p>
          </motion.div>

          <div className="flex flex-col md:flex-row items-stretch md:items-start gap-10 relative">
            <div className="hidden md:block absolute top-10 right-[16.66%] left-[16.66%] h-0.5 bg-grey/20 overflow-hidden z-0">
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, ease: "easeInOut" }}
                style={{ transformOrigin: isRTL ? "right" : "left" }}
                className={`h-full w-full ${isRTL ? "bg-gradient-to-l" : "bg-gradient-to-r"} from-primary to-medium-blue`}
              />
            </div>

            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -6 }}
                className="flex-1 text-center relative z-10"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-primary to-medium-blue rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl shadow-primary/30 ring-8 ring-white">
                  <span className="text-white font-black text-xl">{String(index + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-bold text-dark-grey text-xl mb-2">{step.title}</h3>
                <p className="text-grey text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── App Download Section ─────────────────────────────────────── */}
      <section
        ref={phoneSectionRef}
        className="relative py-24 px-4 bg-gradient-to-r from-primary/10 to-primary/5 overflow-hidden"
      >
        <div className="absolute top-10 start-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl blob-float-slow" />
        <div className="absolute bottom-0 end-10 w-96 h-96 bg-primary/15 rounded-full blur-3xl blob-float" />

        <div className="relative max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex-1 [text-align:start]"
            >
              <h2 className="text-3xl md:text-5xl font-black text-dark-grey mb-4">
                {t("home.downloadTitle")}
              </h2>
              <p className="text-grey leading-relaxed mb-8 text-lg">
                {t("home.downloadDesc")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <StoreButton href={ANDROID_URL} Icon={FaGooglePlay} brand="Google Play" availableOnLabel={t("home.storeAvailableOn")} />
                <StoreButton href={IOS_URL} Icon={FaApple} brand="App Store" availableOnLabel={t("home.storeAvailableOn")} />
              </div>
            </motion.div>

            <motion.div
              style={{ rotate: phoneRotate, y: phoneY }}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="flex-1 flex justify-center perspective-1000"
            >
              <img
                src={phoneImg}
                alt={t("home.phoneImgAlt")}
                className="max-w-full max-h-96 object-contain drop-shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
